import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Divider from "antd/es/divider";
import Form from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import Skeleton from "antd/es/skeleton";
import Switch from "antd/es/switch";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import EmailQuota from "@mapmycustomers/shared/types/email/EmailQuota";
import EmailSyncProgress from "@mapmycustomers/shared/types/email/EmailSyncProgress";
import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import BulkEmailSettingValue from "@mapmycustomers/shared/types/settingValues/BulkEmailSettingValue";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { Alert, LoadingSpinner } from "@mapmycustomers/ui";

import EmailsNotSupported from "@app/component/EmailsNotSupported";
import NylasType from "@app/enum/NylasType";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import {
  doesNeedToOpenActivityFormAfterSendingEmail,
  getEmailQuota,
  getEmailSyncProgress,
} from "@app/store/email";
import {
  fetchEmailQuota,
  fetchEmailSyncProgress,
  showEmailFeatures,
} from "@app/store/email/actions";
import {
  getBulkEmailSetting,
  getMe,
  getOrganization,
  isEmailServiceSupported,
} from "@app/store/iam";
import { updateMetadata, updateSetting } from "@app/store/iam/actions";
import {
  getEmailNylasInfo,
  isInitializing,
  isNylasConnectionFailed,
  isNylasCreating,
  isNylasDeleting,
} from "@app/store/nylas";
import { createNylas, updateNylas } from "@app/store/nylas/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import Iam from "@app/types/Iam";
import NylasInfo from "@app/types/nylas/NylasInfo";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import doesEmailActivityLayoutHaveRequiredFields from "@app/util/email/doesEmailActivityLayoutHaveRequiredFields";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import { activityLayoutModel } from "@app/util/layout/impl";

import settingsAnalytics from "../../settingsAnaytics";
import showSaveSettingSuccessfulMessage from "../../util/showSaveSettingSuccessfulMessage";
import SettingPage from "../common/SettingPage";

import styles from "./EmailPreferences.module.scss";
import messages from "./messages";
import NylasConnect from "./NylasConnect";
import NylasDisconnected from "./NylasConnect/NylasDisconnected";
import NylasErrored from "./NylasConnect/NylasErrored";
import SendingHours from "./SendingHours";
import SignatureText from "./SignatureText";

const EMAIL_SYNC_PROGRESS_REFRESH_INTERVAL = 60 * 1000;

export interface EmailPreferencesForm {
  excludedDays: string[];
  openActivityFormAfterSendingEmail: boolean;
  signature: boolean;
  signatureText: string;
  startTime: string;
  trackEmails: boolean;
  workingHours: [string, string];
}

interface Props {
  bulkEmailSetting?: Setting<BulkEmailSettingValue>;
  emailNylasInfo?: NylasInfo;
  emailQuota?: EmailQuota;
  emailServiceSupported: boolean;
  emailSyncProgress?: EmailSyncProgress;
  isNylasFailed: boolean;
  isNylasProcessing: boolean;
  me: Iam;
  onCreateNylas: typeof createNylas.request;
  onFetchEmailQuota: typeof fetchEmailQuota.request;
  onFetchEmailSyncProgress: typeof fetchEmailSyncProgress.request;
  onShowEmailFeatures: typeof showEmailFeatures;
  onUpdateMetadata: typeof updateMetadata.request;
  onUpdateNylas: typeof updateNylas.request;
  onUpdateSetting: typeof updateSetting.request;
  openActivityFormAfterSendingEmail: boolean;
  organization: Organization;
}

const EmailPreferences: React.FC<Props> = ({
  bulkEmailSetting,
  emailNylasInfo,
  emailQuota,
  emailServiceSupported,
  emailSyncProgress,
  isNylasFailed,
  isNylasProcessing,
  me,
  onCreateNylas,
  onFetchEmailQuota,
  onFetchEmailSyncProgress,
  onShowEmailFeatures,
  onUpdateMetadata,
  onUpdateNylas,
  onUpdateSetting,
  openActivityFormAfterSendingEmail,
  organization,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const [form] = Form.useForm<EmailPreferencesForm>();
  const emailPreferencesAnalytics = useExtendedAnalytics(["Email Preferences"], settingsAnalytics);

  const [isDirty, setDirty, unsetDirty] = useBoolean();
  const [updating, setUpdating, unsetUpdating] = useBoolean();

  useEffect(() => {
    emailPreferencesAnalytics.clicked([]);
  }, [emailPreferencesAnalytics]);

  useEffect(() => {
    if (emailNylasInfo) {
      onFetchEmailSyncProgress();
    }
  }, [emailNylasInfo, onFetchEmailSyncProgress]);

  useEffect(() => {
    if (emailNylasInfo && emailSyncProgress?.syncing) {
      const intervalId = setInterval(() => {
        onFetchEmailSyncProgress();
      }, EMAIL_SYNC_PROGRESS_REFRESH_INTERVAL);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [emailNylasInfo, emailSyncProgress, onFetchEmailSyncProgress]);

  useEffect(() => {
    if (!emailQuota && emailServiceSupported && emailNylasInfo) {
      onFetchEmailQuota();
    }
  }, [emailQuota, emailServiceSupported, emailNylasInfo, onFetchEmailQuota]);

  const signatureEnabled = useWatch("signature", form);
  const openActivityFormAfterSendingEmailEnabled = useWatch(
    "openActivityFormAfterSendingEmail",
    form
  );
  const trackEmailsEnabled = useWatch("trackEmails", form);

  const [formContainerRef, handleFinishFail] = useErroredFieldsScroller();

  useChangeTracking(() => {
    emailPreferencesAnalytics.clicked([
      "Connected Email",
      `Toggled Signature ${signatureEnabled ? "On" : "Off"}`,
    ]);
  }, [signatureEnabled]);

  useChangeTracking(() => {
    emailPreferencesAnalytics.clicked([
      `Toggled Open Activity Form After Sending ${
        openActivityFormAfterSendingEmailEnabled ? "On" : "Off"
      }`,
    ]);
  }, [openActivityFormAfterSendingEmailEnabled]);

  useChangeTracking(() => {
    emailPreferencesAnalytics.clicked([
      `Toggled Show Opens & Clicks ${trackEmailsEnabled ? "On" : "Off"}`,
    ]);
  }, [trackEmailsEnabled]);

  useEffect(() => {
    const params = new Map(new URLSearchParams(window.location.search).entries());
    if (params.size > 0) {
      const authCode = params.get("code");
      if (authCode) {
        if (emailNylasInfo) {
          onUpdateNylas({
            nylas: { ...emailNylasInfo, authCode, nylasCrmActivityTypes: [] },
          });
        } else {
          onCreateNylas({
            callback: () => {
              onFetchEmailQuota({ callback: () => onShowEmailFeatures() });
            },
            nylas: {
              authCode,
              syncOptions: { incoming: false, outgoing: true },
              type: NylasType.EMAIL,
            },
          });
        }
        history.replace(`${Path.SETTINGS}/${SettingPath.PERSONAL_EMAIL_PREFERENCES}`);
      }
    }
  }, [
    onCreateNylas,
    onFetchEmailQuota,
    history,
    emailNylasInfo,
    onShowEmailFeatures,
    onUpdateNylas,
  ]);

  const handleSubmit = useCallback(async () => {
    setUpdating();
    const { openActivityFormAfterSendingEmail, workingHours, ...restValues } =
      form.getFieldsValue();
    onUpdateMetadata({ openActivityFormAfterSendingEmail });
    if (bulkEmailSetting) {
      onUpdateSetting({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          unsetUpdating();
          unsetDirty();
        },
        orgId: organization.id,
        setting: {
          ...bulkEmailSetting,
          value: {
            ...bulkEmailSetting.value,
            ...restValues,
            endTime: workingHours[1],
            startTime: workingHours[0],
          },
        },
        userId: me.id,
      });
    }
  }, [
    bulkEmailSetting,
    form,
    intl,
    me.id,
    onUpdateMetadata,
    onUpdateSetting,
    organization,
    setUpdating,
    unsetDirty,
    unsetUpdating,
  ]);

  const handleUndo = useCallback(() => {
    form.resetFields();
    unsetDirty();
  }, [form, unsetDirty]);

  const openActivityFormAfterSendingEmailDisabled =
    activityLayoutModel.layouts.length > 1 || doesEmailActivityLayoutHaveRequiredFields();

  return (
    <AnalyticsContext.Provider value={emailPreferencesAnalytics}>
      <SettingPage
        changesSaving={updating}
        hasChanges={isDirty}
        onSaveChanges={form.submit}
        onUnDo={handleUndo}
        title={
          <Row align="top" gutter={layout.spacerM}>
            <Col>{intl.formatMessage(messages.title)}</Col>
            {!!emailNylasInfo && !isNylasFailed && emailServiceSupported && (
              <Col className={styles.connectedInfoWrapper}>
                <Tag color="success">{intl.formatMessage(messages.connectedInfo)}</Tag>
              </Col>
            )}
          </Row>
        }
      >
        {!emailServiceSupported ? (
          <EmailsNotSupported className={styles.emailsNotSupported} />
        ) : isNylasProcessing ? (
          <LoadingSpinner global />
        ) : isNylasFailed ? (
          <NylasErrored />
        ) : emailNylasInfo ? (
          <div ref={formContainerRef}>
            <Form
              form={form}
              initialValues={{
                excludedDays: bulkEmailSetting?.value.excludedDays ?? [],
                openActivityFormAfterSendingEmail,
                ...(bulkEmailSetting
                  ? {
                      signature: bulkEmailSetting.value.signature,
                      signatureText: bulkEmailSetting.value.signatureText,
                      trackEmails: bulkEmailSetting.value.trackEmails,
                      workingHours: [
                        bulkEmailSetting.value.startTime,
                        bulkEmailSetting.value.endTime,
                      ],
                    }
                  : {}),
              }}
              onFieldsChange={() => setDirty()}
              onFinish={handleSubmit}
              onFinishFailed={handleFinishFail}
            >
              <div className={styles.container}>
                <NylasConnect onDisconnect={handleUndo} />
                <Row justify="space-between">
                  <Col>
                    <div className={styles.settingLabel}>
                      {intl.formatMessage(messages.emailQueueTitle)}
                    </div>
                    <div className={styles.settingSubLabel}>
                      {emailQuota ? (
                        intl.formatMessage(messages.emailQueueCount, {
                          count: emailQuota.submitted,
                        })
                      ) : (
                        <Skeleton active paragraph={{ rows: 0 }} />
                      )}
                    </div>
                  </Col>
                </Row>
                <Divider className={styles.divider} />
                <Row justify="space-between">
                  <Col>
                    <div
                      className={cn(styles.settingLabel, { [styles.disabled]: !signatureEnabled })}
                    >
                      {intl.formatMessage(messages.signatureTitle)}
                    </div>
                    <div
                      className={cn(styles.settingSubLabel, {
                        [styles.disabled]: !signatureEnabled,
                      })}
                    >
                      {intl.formatMessage(messages.signatureDescription)}
                    </div>
                  </Col>
                  <Col>
                    <Form.Item name="signature" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <SignatureText visible={signatureEnabled} />
                <Row justify="space-between">
                  <SendingHours form={form} />
                </Row>
                <Divider className={styles.divider} />
                <Row justify="space-between">
                  <Col>
                    <div className={cn(styles.settingLabel)}>
                      {intl.formatMessage(messages.openActivityTitle)}
                    </div>
                    <div className={cn(styles.settingSubLabel)}>
                      {intl.formatMessage(messages.openActivityDescription)}
                    </div>
                  </Col>
                  <Col>
                    {openActivityFormAfterSendingEmailDisabled ? (
                      <Tooltip title={intl.formatMessage(messages.openActivityDisabledTooltip)}>
                        <Switch checked disabled />
                      </Tooltip>
                    ) : (
                      <Form.Item name="openActivityFormAfterSendingEmail" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    )}
                  </Col>
                </Row>
                {openActivityFormAfterSendingEmailDisabled && (
                  <Row>
                    <Col>
                      <Alert
                        message={intl.formatMessage(messages.openActivityDisabledAlert)}
                        showIcon
                        type="info"
                      />
                    </Col>
                  </Row>
                )}
                <Row justify="space-between">
                  <Col>
                    <div
                      className={cn(styles.settingLabel, {
                        [styles.disabled]: !trackEmailsEnabled,
                      })}
                    >
                      {intl.formatMessage(messages.showOpensAndClicksTitle)}
                    </div>
                    <div
                      className={cn(styles.settingSubLabel, {
                        [styles.disabled]: !trackEmailsEnabled,
                      })}
                    >
                      {intl.formatMessage(messages.showOpensAndClicksDescription)}
                    </div>
                  </Col>
                  <Col>
                    <Form.Item name="trackEmails" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Form>
          </div>
        ) : (
          <NylasDisconnected />
        )}
      </SettingPage>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  bulkEmailSetting: getBulkEmailSetting(state),
  emailNylasInfo: getEmailNylasInfo(state),
  emailQuota: getEmailQuota(state),
  emailServiceSupported: isEmailServiceSupported(state),
  emailSyncProgress: getEmailSyncProgress(state),
  isNylasFailed: isNylasConnectionFailed(state),
  isNylasProcessing: isNylasCreating(state) || isNylasDeleting(state) || isInitializing(state),
  me: getMe(state)!,
  openActivityFormAfterSendingEmail: doesNeedToOpenActivityFormAfterSendingEmail(state),
  organization: getOrganization(state)!,
});

const mapDispatchToProps = {
  onCreateNylas: createNylas.request,
  onFetchEmailQuota: fetchEmailQuota.request,
  onFetchEmailSyncProgress: fetchEmailSyncProgress.request,
  onShowEmailFeatures: showEmailFeatures,
  onUpdateMetadata: updateMetadata.request,
  onUpdateNylas: updateNylas.request,
  onUpdateSetting: updateSetting.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailPreferences);
